import React from 'react';
import BlurFade from '../primitives/BlurFade';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline } from '../layout/typography';
import { Check, Hammer, Award, MapPin, Phone, Shield, Eye, Flower2 } from 'lucide-react';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

const ICONS = { check: Check, hammer: Hammer, award: Award, map: MapPin, phone: Phone, shield: Shield, eye: Eye, flower: Flower2 };

/**
 * BenefitsBlock — asymmetric clip-shade.de inspired card grid.
 *
 * Top 3 USPs render as PROMINENT hero cards (larger icon, larger headline,
 * more whitespace) on a 3-column row; remaining USPs render as COMPACT
 * cards on a 3-column row below. The hierarchy guides the eye to the
 * strongest selling points first.
 *
 * Layout:
 *   • Mobile (1 col): all cards stacked, top 3 still slightly larger.
 *   • md+ (3 cols):   row 1 = 3 prominent cards | row 2 = 3 compact cards.
 *
 * items: [{ icon, title, sub }] — first 3 are treated as primary.
 */
export default function BenefitsBlock({
  id, testId, tone = 'cream', rhythm = 'relaxed',
  eyebrow, title, items = [],
}) {
  const { lang, overlay } = useLang();
  const primary = items.slice(0, 3);
  const secondary = items.slice(3);

  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container>
        <div className="max-w-3xl mb-12 lg:mb-16">
          {eyebrow && <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>}
          {title && <BlurFade delay={0.05}><Headline size="lg">{title}</Headline></BlurFade>}
        </div>

        {/* Primary row — prominent */}
        {primary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {primary.map((b, i) => {
              const Icon = ICONS[b.icon] || Check;
              return (
                <BlurFade key={b.title} delay={i * 0.06} offset={14}>
                  <div
                    data-testid={`benefit-primary-${i}`}
                    className="group h-full flex flex-col p-7 lg:p-9 rounded-[24px] bg-[#FAF4E6] border border-[#F2DFB3] transition-all duration-400 hover:border-[#F26B1A]/45 hover:shadow-[0_24px_50px_-22px_rgba(242,107,26,0.32)] hover:-translate-y-1"
                  >
                    {b.image && (
                      <div className="relative -mx-2 -mt-2 mb-6 aspect-[4/3] overflow-hidden rounded-[18px] bg-white border border-white/80 shadow-[0_18px_40px_-28px_rgba(31,29,26,0.35)]">
                        <img
                          src={b.image}
                          alt={mediaAlt(overlay, b.image, lang, b.title || '')}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                        />
                      </div>
                    )}
                    <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white border border-[#F26B1A]/25 flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-[#F26B1A]" strokeWidth={2.2} />
                    </div>
                    <h3 className="font-display text-[#1F1D1A] text-[24px] lg:text-[30px] leading-[1.1]">
                      {b.title}
                    </h3>
                    {b.sub && (
                      <p className="mt-3 text-[#5C5852] text-[14.5px] lg:text-[15.5px] leading-relaxed">
                        {b.sub}
                      </p>
                    )}
                  </div>
                </BlurFade>
              );
            })}
          </div>
        )}

        {/* Secondary row — compact */}
        {secondary.length > 0 && (
          <div className="mt-4 lg:mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {secondary.map((b, i) => {
              const Icon = ICONS[b.icon] || Check;
              return (
                <BlurFade key={b.title} delay={i * 0.06} offset={14}>
                  <div
                    data-testid={`benefit-secondary-${i}`}
                    className="group h-full flex items-start gap-4 p-5 lg:p-6 rounded-[18px] bg-white border border-[#E5E0D2] transition-all duration-400 hover:border-[#F26B1A]/40 hover:shadow-[0_14px_30px_-18px_rgba(31,29,26,0.25)] hover:-translate-y-0.5"
                  >
                    {b.image ? (
                      <div className="relative shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-[14px] overflow-hidden bg-[#FAF4E6] border border-[#F2DFB3]">
                        <img
                          src={b.image}
                          alt={mediaAlt(overlay, b.image, lang, b.title || '')}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                        />
                        <div className="absolute bottom-1.5 left-1.5 w-7 h-7 rounded-full bg-white/95 border border-[#F26B1A]/20 flex items-center justify-center shadow-[0_6px_14px_-8px_rgba(31,29,26,0.35)]">
                          <Icon className="w-3.5 h-3.5 text-[#F26B1A]" strokeWidth={2.4} />
                        </div>
                      </div>
                    ) : (
                      <div className="shrink-0 w-10 h-10 rounded-full bg-[#FAF4E6] border border-[#F2DFB3] flex items-center justify-center mt-0.5">
                        <Icon className="w-4 h-4 text-[#F26B1A]" strokeWidth={2.4} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display text-[#1F1D1A] text-[16px] lg:text-[18px] leading-tight">
                        {b.title}
                      </h4>
                      {b.sub && (
                        <p className="mt-1.5 text-[#5C5852] text-[13px] lg:text-[13.5px] leading-relaxed">
                          {b.sub}
                        </p>
                      )}
                    </div>
                  </div>
                </BlurFade>
              );
            })}
          </div>
        )}
      </Container>
    </Section>
  );
}
